import { useEffect, useId, useState, useCallback } from "react";
import { api, ApiError } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";

// ---- types ----------------------------------------------------------------

export interface Connection {
  id: string;
  tenant_id: string;
  name: string;
  type: "postgres" | "mysql" | "mongo" | "http";
  host?: string;
  port?: number;
  username?: string;
  params?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface ConnectionFormValues {
  name: string;
  type: Connection["type"];
  host: string;
  port: string;
  username: string;
  password: string;
  params: string; // JSON string
}

const defaultForm = (): ConnectionFormValues => ({
  name: "",
  type: "postgres",
  host: "",
  port: "",
  username: "",
  password: "",
  params: "{}",
});

// ---- helpers ---------------------------------------------------------------

function typeLabel(t: Connection["type"]) {
  return { postgres: "PostgreSQL", mysql: "MySQL", mongo: "MongoDB", http: "HTTP" }[t] ?? t;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ---- DrawerForm ------------------------------------------------------------

function ConnectionDrawer({
  editing,
  onClose,
  onSaved,
}: {
  editing: Connection | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ConnectionFormValues>(() => {
    if (editing) {
      return {
        name: editing.name,
        type: editing.type,
        host: editing.host ?? "",
        port: editing.port != null ? String(editing.port) : "",
        username: editing.username ?? "",
        password: "", // never pre-filled for security
        params: editing.params ? JSON.stringify(editing.params, null, 2) : "{}",
      };
    }
    return defaultForm();
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Prefix for htmlFor/id pairs. The drawer can be mounted more than once over
  // a page's lifetime, so ids have to be instance-scoped rather than literals.
  const fid = useId();

  const set = (k: keyof ConnectionFormValues, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    let params: Record<string, unknown> = {};
    try {
      params = JSON.parse(form.params || "{}");
    } catch {
      setError("Params must be valid JSON");
      setSaving(false);
      return;
    }

    const body: Record<string, unknown> = {
      name: form.name,
      type: form.type,
      host: form.host || undefined,
      port: form.port ? parseInt(form.port, 10) : undefined,
      username: form.username || undefined,
      params,
    };
    if (form.password) {
      body.password = form.password;
    }

    try {
      if (editing) {
        await api.patch(`/connections/${editing.id}`, body);
      } else {
        await api.post("/connections", body);
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  const labelCls = "block text-sm font-medium mb-1";

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <aside className="relative ml-auto w-full max-w-md bg-background shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {editing ? "Edit connection" : "New connection"}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
        >
          {error && (
            <div className="rounded-md bg-destructive/10 text-destructive text-sm px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className={labelCls} htmlFor={`${fid}-name`}>
              Name *
            </label>
            <input
              id={`${fid}-name`}
              className={inputCls}
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="prod-postgres-eu"
            />
          </div>

          <div>
            <label className={labelCls} htmlFor={`${fid}-type`}>
              Type *
            </label>
            <select
              id={`${fid}-type`}
              className={inputCls}
              value={form.type}
              onChange={(e) => set("type", e.target.value as Connection["type"])}
            >
              <option value="postgres">PostgreSQL</option>
              <option value="mysql">MySQL</option>
              <option value="mongo">MongoDB</option>
              <option value="http">HTTP</option>
            </select>
          </div>

          {form.type !== "http" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} htmlFor={`${fid}-host`}>
                  Host
                </label>
                <input
                  id={`${fid}-host`}
                  className={inputCls}
                  value={form.host}
                  onChange={(e) => set("host", e.target.value)}
                  placeholder="db.example.com"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor={`${fid}-port`}>
                  Port
                </label>
                <input
                  id={`${fid}-port`}
                  className={inputCls}
                  type="number"
                  value={form.port}
                  onChange={(e) => set("port", e.target.value)}
                  placeholder={
                    form.type === "postgres"
                      ? "5432"
                      : form.type === "mysql"
                      ? "3306"
                      : "27017"
                  }
                />
              </div>
            </div>
          )}

          <div>
            <label className={labelCls} htmlFor={`${fid}-username`}>
              Username
            </label>
            <input
              id={`${fid}-username`}
              className={inputCls}
              value={form.username}
              onChange={(e) => set("username", e.target.value)}
              placeholder={form.type === "http" ? "Leave blank for bearer token" : ""}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor={`${fid}-password`}>
              Password / Token
              {editing && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (leave blank to keep existing)
                </span>
              )}
            </label>
            <input
              id={`${fid}-password`}
              className={inputCls}
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className={labelCls} htmlFor={`${fid}-params`}>
              Extra params (JSON)
              <span className="ml-2 text-xs text-muted-foreground">
                {form.type === "postgres" && "e.g. {\"db_name\":\"mydb\",\"ssl_mode\":\"require\"}"}
                {form.type === "mysql" && "e.g. {\"db_name\":\"mydb\"}"}
                {form.type === "mongo" && "e.g. {\"db_name\":\"mydb\",\"replica_set\":\"rs0\"}"}
                {form.type === "http" && "e.g. {\"base_url\":\"https://api.example.com\"}"}
              </span>
            </label>
            <textarea
              id={`${fid}-params`}
              className={`${inputCls} font-mono text-xs`}
              rows={4}
              value={form.params}
              onChange={(e) => set("params", e.target.value)}
            />
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create connection"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 rounded-md border border-input text-sm hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// ---- Main page -------------------------------------------------------------

export default function ConnectionsPage() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Connection | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<{ id: string; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Connection[]>("/connections");
      setConnections(data ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load connections");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (conn: Connection) => {
    setEditing(conn);
    setDrawerOpen(true);
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    setDeleteError(null);
    try {
      await api.del(`/connections/${id}`);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError({ id, msg: err.message });
      } else {
        setDeleteError({ id, msg: "Failed to delete" });
      }
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Connections</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Reusable host/credentials shared across multiple checks. Rotate a
              password once — all checks using it update automatically.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
          >
            + New connection
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive text-sm px-4 py-3 mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground py-12 text-center">
            Loading…
          </div>
        ) : connections.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-16 text-center">
            <p className="text-muted-foreground text-sm">No connections yet.</p>
            <button
              onClick={openCreate}
              className="mt-3 text-sm underline text-primary hover:text-primary/80"
            >
              Create your first connection
            </button>
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">
                    Host
                  </th>
                  <th className="text-left px-4 py-3 font-medium hidden md:table-cell">
                    Created
                  </th>
                  <th className="text-right px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {connections.map((conn) => (
                  <tr
                    key={conn.id}
                    className="border-t border-border hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium">
                      {conn.name}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {typeLabel(conn.type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      {conn.host ?? "—"}
                      {conn.port != null && (
                        <span className="text-xs">:{conn.port}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                      {formatDate(conn.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(conn)}
                          className="text-xs text-primary hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(conn.id)}
                          disabled={deleting === conn.id}
                          className="text-xs text-destructive hover:underline disabled:opacity-50"
                        >
                          {deleting === conn.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                      {deleteError?.id === conn.id && (
                        <div className="text-xs text-destructive mt-1 text-right max-w-xs ml-auto">
                          {deleteError.msg}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {drawerOpen && (
        <ConnectionDrawer
          editing={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
